import { useEffect, useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';

export type LinkPreviewData = {
  url: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  siteName?: string;
};

const cache = new Map<string, LinkPreviewData | undefined>();
const BATCH_DELAY = 100;

let batchUrls: string[] = [];
let batchCallbacks: Array<() => void> = [];
let batchTimer: ReturnType<typeof setTimeout> | undefined;

function flushBatch() {
  const urls = [...new Set(batchUrls)];
  const callbacks = [...batchCallbacks];
  batchUrls = [];
  batchCallbacks = [];
  batchTimer = undefined;

  if (urls.length === 0) {
    return;
  }

  rpx.linkPreviews
    .getLinkPreviews({ urls: urls.slice(0, 20) })
    .then((result) => {
      for (const url of urls) {
        if (url in result) {
          cache.set(url, result[url] as LinkPreviewData);
        } else {
          cache.set(url, undefined);
        }
      }
      for (const cb of callbacks) {
        cb();
      }
    })
    .catch(() => {
      for (const url of urls) {
        cache.set(url, undefined);
      }
      for (const cb of callbacks) {
        cb();
      }
    });
}

function requestPreviews(urls: string[], callback: () => void) {
  batchUrls.push(...urls);
  batchCallbacks.push(callback);
  clearTimeout(batchTimer);
  batchTimer = setTimeout(flushBatch, BATCH_DELAY);
}

function extractExternalBareUrls(html: string): string[] {
  if (!html) {
    return [];
  }
  const urls: string[] = [];
  const regex = /<a\s+[^>]*href=["']([^"']+)["'][^>]*>([^<]*)<\/a>/gi;
  const { origin } = window.location;
  for (const match of html.matchAll(regex)) {
    const [, href, text] = match;
    if (!href || !text) {
      continue;
    }
    if (href.startsWith(origin)) {
      continue;
    }
    const trimmedText = text.trim();
    try {
      const decodedHref = decodeURIComponent(href).replace(/\/$/, '');
      const isTextTheUrl =
        trimmedText === href ||
        trimmedText === href.replace(/\/$/, '') ||
        trimmedText === decodedHref ||
        trimmedText === decodedHref.replace(/\/$/, '');
      if (isTextTheUrl) {
        const parsed = new URL(href);
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
          urls.push(href);
        }
      }
    } catch {
      // not a valid URL
    }
  }
  return [...new Set(urls)];
}

export function useLinkPreviews(html?: string): Record<string, LinkPreviewData> {
  const [previews, setPreviews] = useState<Record<string, LinkPreviewData>>({});

  useEffect(() => {
    const urls = extractExternalBareUrls(html || '');
    if (urls.length === 0) {
      setPreviews({});
      return;
    }

    const result: Record<string, LinkPreviewData> = {};
    const uncached: string[] = [];
    for (const url of urls) {
      if (cache.has(url)) {
        const cached = cache.get(url);
        if (cached) {
          result[url] = cached;
        }
      } else {
        uncached.push(url);
      }
    }

    if (Object.keys(result).length > 0) {
      setPreviews(result);
    }

    if (uncached.length === 0) {
      return;
    }

    let cancelled = false;
    requestPreviews(uncached, () => {
      if (cancelled) {
        return;
      }
      const updated: Record<string, LinkPreviewData> = {};
      for (const url of urls) {
        const cached = cache.get(url);
        if (cached) {
          updated[url] = cached;
        }
      }
      setPreviews(updated);
    });

    return () => {
      cancelled = true;
    };
  }, [html]);

  return previews;
}
